// Der Baum des Bereichs Claude in drei Abschnitten (docs/claude.md, Mockup
// docs/mockups/claude-baum.html, Stand 02 -- Tim 16.10.2026: „Wir nehmen
// die Empfehlung"): Chats · Änderungen · Verbrauch, je mit Lage-Satz im Kopf.
//
// „Änderungen" zeigt EIN Repo (Tim: „immer nur das Repo, welches gerade im
// Tab oder im Baum aktiv/ausgewählt ist"): das des Chats im aktiven Tab --
// oder das im Baum gewählte, solange man nicht in einen anderen Tab wechselt.
// Die reinen Regeln stehen in claude.ts (geprüft in claude.test.mjs).

import { getClaude } from "../claude-summary.svelte";
import type { IconName } from "../Icon.svelte";
import { anteilBedeutung, zugriffeLage } from "../gedaechtnis/gedaechtnis";
import { getGedaechtnis, startGedaechtnis } from "../gedaechtnis-summary.svelte";
import { t } from "../i18n.svelte";
import type { LageTeil } from "../lage";
import type { BaumKnoten } from "../shell/baum-knoten.svelte";
import { oeffnePfad } from "../shell/route-sync.svelte";
import { aktiverTab, istPfadAktiv, istSeiteOffen } from "../shell/tabs.svelte";
import { tageslimitJetzt } from "./tageslimit.svelte";
import { aenderungenLageAus, chatsLageAus, commitsLageAus, repoFuerAenderungen, verbrauchLageAus } from "./claude";

let wahl = $state<{ repo: string; pfad: string } | null>(null);

function aktiverPfad(): string {
  const t = aktiverTab();
  return t && t.typ === "page" ? String(t.params.path ?? "") : "";
}

/** Im Baum ein Repo gewählt: „Änderungen" zeigt es, bis ein anderer Tab
 *  aktiv wird. */
export function waehleRepo(repo: string): void {
  wahl = { repo, pfad: aktiverPfad() };
}

/** Welches Repo „Änderungen" zeigt. */
export function gezeigtesRepo(): string {
  const st = getClaude()?.stand;
  return st ? repoFuerAenderungen(st, aktiverPfad(), wahl) : "";
}

export function chatsLage(): LageTeil[] {
  const st = getClaude()?.stand;
  return chatsLageAus(st?.chats ?? [], st?.anmeldung);
}

export function aenderungenLage(): LageTeil[] {
  const st = getClaude()?.stand;
  return st ? aenderungenLageAus(st, gezeigtesRepo()) : [];
}

export function commitsLage(): LageTeil[] {
  const st = getClaude()?.stand;
  return st ? commitsLageAus(st, gezeigtesRepo(), new Date()) : [];
}

export function verbrauchLage(): LageTeil[] {
  const v = getClaude()?.stand.verbrauch;
  return verbrauchLageAus(v, tageslimitJetzt(v, new Date()));
}

/** Oben im Baum, vor den Abschnitten: die beiden Seiten des Bereichs (Tim
 *  21.10.2026: „über den Chats noch die Übersicht und das Gedächtnis als
 *  Seite eintragen. Dann kommt man schnell hin. Mit dem Gedächtnis ist das
 *  gerade schwierig"). Vorher stand „Übersicht →" im Kopf und das
 *  Gedächtnis als angedockter Abschnitt mit Suchfeld ganz unten. Am
 *  Gedächtnis rechts, wie weit die Bedeutung gerechnet ist -- nur solange
 *  es nicht fertig ist. */
export function claudeSeiten(): BaumKnoten[] {
  // Erst, wenn der Baum gezeichnet wird (nicht beim Boot jeder Seite);
  // startGedaechtnis darf mitten im Zeichnen gerufen werden.
  startGedaechtnis();
  const gd = getGedaechtnis();
  let tail: string | undefined;
  let status: BaumKnoten["status"];
  if (gd?.fehler) status = "bad";
  else if (gd?.einbetten === "wartet") status = "warn";
  if (gd && gd.offen > 0) {
    tail = `${Math.floor(anteilBedeutung(gd) * 100)} %`;
    if (!status && gd.einbetten === "laeuft") status = "laeuft";
  }
  const seite = (pfad: string, label: string, icon: IconName, extra: Partial<BaumKnoten> = {}): BaumKnoten => ({
    id: "seite:" + pfad,
    label,
    href: pfad,
    icon,
    oeffnen: () => oeffnePfad(pfad, { vorschau: true }),
    anheften: () => oeffnePfad(pfad),
    offen: istSeiteOffen(pfad),
    aktiv: istPfadAktiv(pfad),
    ...extra,
  });
  return [
    seite("/claude", t("claude.uebersicht", "Übersicht"), "claude"),
    seite("/claude/gedaechtnis", t("gedaechtnis.title", "Gedächtnis"), "history", { tail, status, ohnePunkt: status === "laeuft" }),
  ];
}

/** Abschnitt „Gedächtnis" unten (aufgeklappt): wie viel gelesen und
 *  geschrieben wurde; zugeklappt stehen Lupe und Stift (BaumZu). */
export function gedaechtnisLage(): LageTeil[] {
  startGedaechtnis();
  return zugriffeLage(getGedaechtnis(), {
    laedt: t("claude.laedt_stand", "wird gelesen …"),
    fehler: t("gedaechtnis.lage.fehler", "Einlesen gescheitert"),
    bedeutung: (p) => t("gedaechtnis.lage.bedeutung_kurz", "Bedeutung {p}").replace("{p}", p),
    stunde: (l, s) => t("gedaechtnis.lage.stunde", "{l} gelesen · {s} geschrieben in 1 h").replace("{l}", String(l)).replace("{s}", String(s)),
    still: t("gedaechtnis.lage.still", "still in der letzten Stunde"),
  });
}
